import { z } from 'zod';
/**
 * Validador de CNPJ (14 dígitos)
 */
export const cnpjRegex = /^\d{14}$/;
/**
 * Validador de CPF (11 dígitos)
 */
export const cpfRegex = /^\d{11}$/;
/**
 * Schema de Atualização das Configurações Fiscais da Empresa
 */
export const FiscalSettingsUpdateSchema = z.object({
    tenantCnpj: z.string().transform(v => v.replace(/\D/g, '')).refine(v => cnpjRegex.test(v), {
        message: 'CNPJ deve conter 14 dígitos numéricos válidos.'
    }),
    razaoSocial: z.string().min(2, 'Razão Social é obrigatória (mínimo 2 caracteres).'),
    nomeFantasia: z.string().optional(),
    inscricaoEstadual: z.string().min(2, 'Inscrição Estadual é obrigatória.'),
    inscricaoMunicipal: z.string().optional(),
    crt: z.union([z.literal(1), z.literal(2), z.literal(3)]).default(1),
    regimeTributarioEspecial: z.number().int().optional(),
    logradouro: z.string().min(2, 'Logradouro é obrigatório.'),
    numero: z.string().default('S/N'),
    complemento: z.string().optional(),
    bairro: z.string().min(2, 'Bairro é obrigatório.'),
    codigoMunicipio: z.string().transform(v => v.replace(/\D/g, '')).refine(v => v.length === 7, {
        message: 'Código do município do IBGE deve conter 7 dígitos.'
    }),
    municipio: z.string().min(2, 'Município é obrigatório.'),
    uf: z.string().length(2, 'UF deve conter exatamente 2 letras.').toUpperCase(),
    cep: z.string().transform(v => v.replace(/\D/g, '')).refine(v => v.length === 8, {
        message: 'CEP deve conter 8 dígitos numéricos.'
    }),
    ambiente: z.union([z.literal(1), z.literal(2)]).default(2),
    cscTokenId: z.string().optional(),
    cscTokenValue: z.string().optional(),
    certificatePfxBase64: z.string().optional(),
    certificatePassword: z.string().optional(),
    seriePadraoNfce: z.number().int().positive().default(1),
    seriePadraoNfe55: z.number().int().positive().default(1),
    seriePadraoNfse: z.number().int().positive().default(1),
    timeoutSefazSeconds: z.number().int().min(2).max(60).default(5),
    nfceEnabled: z.boolean().default(true),
    nfeModel55Enabled: z.boolean().default(false),
    nfseEnabled: z.boolean().default(false),
});
/**
 * Schema de Emissão de NFS-e (Padrão Nacional ADN)
 */
export const NfseIssuePayloadSchema = z.object({
    referenceId: z.string().optional(),
    serie: z.number().int().positive().default(1),
    informacoesComplementares: z.string().max(1000).optional(),
    tomador: z.object({
        documento: z.string().transform(v => v.replace(/\D/g, '')).refine(v => v.length === 11 || v.length === 14, {
            message: 'Documento do tomador deve ser um CPF (11) ou CNPJ (14) válido.'
        }),
        tipoDocumento: z.enum(['cpf', 'cnpj']),
        nome: z.string().min(2, 'Nome/Razão Social do tomador é obrigatório.'),
        email: z.string().email('E-mail do tomador inválido.').optional().or(z.literal('')),
        telefone: z.string().optional(),
        endereco: z.object({
            logradouro: z.string().optional(),
            numero: z.string().optional(),
            complemento: z.string().optional(),
            bairro: z.string().optional(),
            codigoMunicipio: z.string().optional(),
            municipio: z.string().optional(),
            uf: z.string().length(2).optional(),
            cep: z.string().optional()
        }).optional()
    }),
    servico: z.object({
        codigoTributacaoNacional: z.string().min(1, 'Código de tributação nacional ou LC 116 é obrigatório.'),
        cnae: z.string().optional(),
        discriminacao: z.string().min(5, 'Discriminação do serviço deve ter ao menos 5 caracteres.'),
        valorServico: z.number().positive('Valor do serviço deve ser maior que zero.'),
        issRetido: z.boolean().default(false),
        aliquotaIss: z.number().min(0).max(100).optional(),
        valorDeducoes: z.number().min(0).optional(),
        codigoMunicipioPrestacao: z.string().optional()
    }),
    regimeSimples: z.boolean().default(true)
});
/**
 * Schema de Cancelamento Fiscal Unificado (NFC-e, NF-e 55 e NFS-e)
 */
export const FiscalCancelPayloadSchema = z.object({
    documentId: z.string().optional(),
    chaveAcesso: z.string().min(10, 'Chave de acesso é obrigatória.'),
    modelo: z.enum(['55', '65', 'nfse']),
    protocolo: z.string().optional(),
    justificativa: z.string()
        .min(15, 'Justificativa legal deve conter no mínimo 15 caracteres.')
        .max(255, 'Justificativa legal deve conter no máximo 255 caracteres.')
});
/**
 * Schema de Inutilização de Numeração (NF-e Modelo 55 / 65)
 */
export const FiscalInutPayloadSchema = z.object({
    ano: z.number().int().min(20).max(99).or(z.number().int().min(2020).max(2099)),
    modelo: z.enum(['55', '65']),
    serie: z.number().int().positive(),
    numeroInicial: z.number().int().positive(),
    numeroFinal: z.number().int().positive(),
    justificativa: z.string()
        .min(15, 'Justificativa de inutilização deve conter no mínimo 15 caracteres.')
        .max(255, 'Justificativa de inutilização deve conter no máximo 255 caracteres.')
}).refine(data => data.numeroFinal >= data.numeroInicial, {
    message: 'Número final deve ser maior ou igual ao número inicial.',
    path: ['numeroFinal']
});
/**
 * Schema de Carta de Correção Eletrônica (CC-e)
 */
export const FiscalCcePayloadSchema = z.object({
    chaveAcesso: z.string().transform(v => v.replace(/\D/g, '')).refine(v => v.length === 44, {
        message: 'Chave de acesso da NF-e deve conter exatamente 44 dígitos.'
    }),
    sequenciaEvento: z.number().int().min(1).max(20).default(1),
    correcao: z.string()
        .min(15, 'Texto da correção deve conter no mínimo 15 caracteres.')
        .max(1000, 'Texto da correção deve conter no máximo 1000 caracteres.')
});
/**
 * Schema de Emissão de NF-e Modelo 55 (B2B, Atacado e Mercadorias)
 */
export const Nfe55SalePayloadSchema = z.object({
    referenceId: z.string().optional(),
    serie: z.number().int().positive().default(1),
    naturezaOperacao: z.string().default('VENDA DE MERCADORIA'),
    indicadorPresenca: z.enum(['0', '1', '2', '3', '4', '5', '9']).default('1'),
    consumidorFinal: z.boolean().optional(),
    destinatario: z.object({
        documento: z.string().transform(v => v.replace(/\D/g, '')).refine(v => v.length === 11 || v.length === 14, {
            message: 'Documento do destinatário deve ser um CPF (11) ou CNPJ (14) válido.'
        }),
        nome: z.string().min(2, 'Nome / Razão Social do destinatário é obrigatório.'),
        indicadorIEDest: z.enum(['1', '2', '9']).default('9'),
        ie: z.string().optional(),
        email: z.string().email().optional().or(z.literal('')),
        endereco: z.object({
            logradouro: z.string().min(1, 'Logradouro do destinatário é obrigatório.'),
            numero: z.string().default('S/N'),
            complemento: z.string().optional(),
            bairro: z.string().min(1, 'Bairro do destinatário é obrigatório.'),
            codigoMunicipio: z.string().transform(v => v.replace(/\D/g, '')).refine(v => v.length === 7, {
                message: 'Código IBGE do município de destino deve ter 7 dígitos.'
            }),
            municipio: z.string().min(2, 'Município do destinatário é obrigatório.'),
            uf: z.string().length(2, 'UF do destinatário deve ter 2 caracteres.').toUpperCase(),
            cep: z.string().transform(v => v.replace(/\D/g, '')).refine(v => v.length === 8, {
                message: 'CEP do destinatário deve ter 8 dígitos.'
            })
        })
    }),
    itens: z.array(z.object({
        cProd: z.union([z.string(), z.number()]).transform(String),
        cEAN: z.string().default('SEM GTIN'),
        xProd: z.string().min(1, 'Descrição do produto é obrigatória.'),
        ncm: z.string().transform(v => v.replace(/\D/g, '')).refine(v => v.length === 8, {
            message: 'NCM deve conter exatamente 8 dígitos.'
        }),
        cest: z.string().optional(),
        cfop: z.string().transform(v => v.replace(/\D/g, '')).optional(),
        uCom: z.string().min(1).default('UN'),
        qCom: z.number().positive('Quantidade comercial deve ser positiva.'),
        vUnCom: z.number().positive('Valor unitário deve ser positivo.'),
        vDesc: z.number().min(0).optional(),
        csosn: z.string().optional(),
        tributos: z.object({
            aliqIcms: z.number().min(0).optional(),
            aliqPis: z.number().min(0).optional(),
            aliqCofins: z.number().min(0).optional(),
            pisCst: z.string().optional(),
            cofinsCst: z.string().optional(),
            cbsCst: z.string().optional(),
            cClassTrib: z.string().optional(),
            aliqCbs: z.number().min(0).optional(),
            aliqIbs: z.number().min(0).optional()
        }).optional()
    })).min(1, 'A NF-e Modelo 55 deve conter pelo menos 1 item na lista.'),
    pagamentos: z.array(z.object({
        forma: z.string().default('01'),
        valor: z.number().positive('Valor do pagamento deve ser positivo.'),
        detalhesCartao: z.any().optional()
    })).optional(),
    transporte: z.object({
        modFrete: z.enum(['0', '1', '2', '3', '4', '9']).default('9'),
        valorFrete: z.number().min(0).optional(),
        valorSeguro: z.number().min(0).optional(),
        outrasDespesas: z.number().min(0).optional(),
        transportadora: z.object({
            cnpjCpf: z.string().optional(),
            xNome: z.string().optional(),
            ie: z.string().optional(),
            xEnder: z.string().optional(),
            xMun: z.string().optional(),
            uf: z.string().length(2).optional()
        }).optional(),
        veiculo: z.object({
            placa: z.string().optional(),
            uf: z.string().length(2).optional()
        }).optional(),
        volumes: z.object({
            qVol: z.number().optional(),
            esp: z.string().optional(),
            marca: z.string().optional(),
            nVol: z.string().optional(),
            pesoL: z.number().optional(),
            pesoB: z.number().optional()
        }).optional()
    }).optional(),
    cobranca: z.object({
        fatura: z.object({
            nFat: z.string(),
            vOrig: z.number(),
            vDesc: z.number().optional(),
            vLiq: z.number()
        }).optional(),
        duplicatas: z.array(z.object({
            nDup: z.string(),
            dVenc: z.string(),
            vDup: z.number()
        })).optional()
    }).optional(),
    informacoesComplementares: z.string().max(2000).optional()
});
